// Interface language.
//
// What translates and what does not: the app's own chrome translates; the
// CONTENT of a card never does. A name, a job title, a company, a promo line —
// those are what their owner typed, and machine-shifting them would put words
// in somebody's mouth on a card they hand to strangers. So a Bangla-speaking
// user reading an English card sees English on the card and Bangla around it,
// which is correct.
//
// No i18n library. The catalog is a plain object and lookup is a function call;
// @lit/localize would add a build-time toolchain and a dependency outside the
// pinned set for something this size. Components re-render through a Lit
// reactive controller, so switching language never needs a reload.
import type { ReactiveController, ReactiveControllerHost } from 'lit';

/** Languages the interface is available in, in the order a picker shows them. */
export const LOCALES = [
  { code: 'en', label: 'English', native: 'English' },
  { code: 'bn', label: 'Bangla', native: 'বাংলা' },
] as const;

export type Locale = (typeof LOCALES)[number]['code'];

const STORAGE_KEY = 'smartcard-locale';
const DEFAULT: Locale = 'en';

/** Message keys, defined by the English catalog — every other language must
 *  supply exactly these, or a build fails rather than a string goes missing. */
const en = {
  // --- shell + page ---
  'app.title': 'Cards',
  'nav.work': 'Company',
  'nav.home': 'My cards',
  // The cards you have collected. Short because it sits in a bar with two
  // other controls, and "Collected cards" wraps on a narrow phone.
  'nav.cards': 'Cards',
  'mine.delete': 'Delete',
  'mine.deleted': 'Deleted {label}.',
  'mine.many': 'Your cards',
  'mine.edit': 'Edit this card',
  'mine.create': 'New card',
  'mine.deck': 'Your cards',
  'mine.nOfTotal': '{n} of {total}',
  'mine.goToCard': 'Show {name}',
  'mine.loading': 'Loading…',
  'dir.title': 'Collected',
  'dir.collect': 'Collect',
  'dir.search': 'Search {n} cards',
  'dir.searchOne': 'Search 1 card',
  'dir.all': 'All',
  'dir.empty': 'No cards yet — tap someone’s card to collect it.',
  'dir.export': 'Export',
  'dir.exportHint': 'Download the contacts shown, as a CSV file',
  'dir.noteAdd': 'Add a note',
  'dir.noteSave': 'Save',
  'dir.noteCancel': 'Cancel',
  'dir.notePlaceholder': 'Where you met, what to follow up — only you can see this.',
  'dir.noteAria': 'Your private note about {name}',
  'dir.tagPlaceholder': 'Tags, separated by commas — expo, follow up',
  'dir.tagAria': 'Your tags for {name}',
  'dir.tagFilterLabel': 'Filter by your tags',
  'dir.tagFilterHint': 'Show only {tag}',
  'dir.tagAll': 'All',
  'dir.noMatch': 'Nothing matches that.',
  'work.title': 'Company',
  'org.makeCardFirst': 'Start — make my card',
  'org.claimCardFirst': 'Start — confirm my card',
  'org.needIdentity': 'First make your own card and confirm it with the code. A company is administered by a person, so there has to be one before you can set it up.',

  // --- a tapped card (seen by strangers, so the most important) ---
  'recv.shared': 'Shared card',
  'recv.saved': 'Saved to your cards',
  'recv.seeAll': 'see all {n}',
  'recv.saveContact': 'Save to phone contacts',
  'recv.tapHint': 'Tap a row on the card to call, email or open it.',
  'recv.savedContact': 'Added to your contacts',
  'recv.mine': 'This is my card',
  'recv.noApp': 'No app needed — this opened straight from a tap.',
  'recv.report': 'Report this card',
  'card.guest': 'Guest',
  'ex.social': 'Profiles',
  'dir.tools': 'Menu',
  'dir.notes': 'Notes',
  'notes.take': 'Take a note…',
  'notes.ph': 'Write anything — it saves as you type, and stays on this device.',
  'notes.empty': 'No notes yet.',
  'notes.noMatch': 'No note matches that.',
  'notes.search': 'Search notes',
  'notes.delete': 'Delete',
  'notes.share': 'Share',
  'notes.copied': 'Copied',
  'edit.useExisting': 'Use an existing image',
  'org.events': 'Events',
  'org.evSub': 'Badges for people who are not staff — guests at a fair, attendees at a training day. They work like cards, never carry your verified mark, and die when the event ends.',
  'org.evNew': 'New event',
  'org.evName': 'Event name',
  'org.evStart': 'Starts',
  'org.evEnd': 'Ends',
  'org.evCreate': 'Create',
  'org.evNone': 'No events yet.',
  'org.evEnded': 'Ended',
  'org.evAttendees': '{n} attendees',
  'org.evCounts': '{people} attendees · {here} checked in',
  'org.evPastePh': 'Paste attendees — any email works here: name, email per line, or cells from a spreadsheet',
  'org.evImport': 'Add attendees',
  'org.evImported': '{n} badges ready',
  'org.evPrint': 'Print badges',
  'org.evScan': 'Scan badges',
  'org.evStopScan': 'Stop scanning',
  'org.evManualPh': 'Badge code (under the QR)',
  'org.evCheckin': 'Check in',
  'org.evWelcome': 'Welcome, {name}',
  'org.evWelcomeBack': 'Welcome back, {name}',
  'org.evDeduped': '{name} is already checked in',
  'org.evCamera': 'The camera could not start — type the badge code instead.',
  'org.evEndNow': 'End event',
  'org.evEndTitle': 'End “{name}” now?',
  'org.evEndBody': 'Every badge stops working immediately, and this cannot be undone.',
  'org.evEndGo': 'End it now',
  'org.evEdit': 'Edit',
  'org.evSave': 'Save changes',
  'org.evUpdated': 'Event updated.',
  'org.evDelete': 'Delete',
  'org.evDeleteTitle': 'Delete “{name}”?',
  'org.evDeleteBody': 'It never issued a badge, so there is nothing to keep. This cannot be undone.',
  'org.evDeleteGo': 'Delete it',
  'org.evExport': 'Attendance CSV',
  'recv.checkinNote': 'This badge checks you in at {event}.',
  'report.title': 'Report this card',
  'report.sub': 'Nothing about you is sent — only which card and why.',
  'report.r.spam': 'Spam',
  'report.r.abuse': 'Abusive content',
  'report.r.impersonation': 'Impersonation',
  'report.r.illegal': 'Illegal content',
  'report.r.other': 'Something else',
  'report.detailsPh': 'Anything that helps us look into it (optional)',
  'report.send': 'Send report',
  'report.sending': 'Sending…',
  'report.cancel': 'Cancel',
  'report.failed': 'Could not send just now — please try again.',
  'report.thanks': 'Thank you. We will take a look.',
  'report.close': 'Close',
  'legal.partOf': 'Part of {parent}',
  'legal.privacy': 'Privacy',
  'legal.terms': 'Terms',
  'legal.notice': 'Legal notice',
  'legal.contact': 'Contact',
  'recv.pitchCta': 'Create my card',
  'recv.keepHead': 'Keep your cards',
  'recv.keepHint': 'Add your number and every card you collect follows you to any device.',
  'recv.keepPh': 'your number',
  'recv.keepCta': 'Send a code',
  'recv.keepSent': 'We sent a code to {where}.',
  'recv.keepConfirm': 'Confirm',
  'recv.keepFail': 'Enter a valid number or address.',
  'recv.yourCards': 'Your collection',
  'recv.notFound': 'Card not found or you’re offline.',
  // A card its company issued, waiting for the person it belongs to. Says
  // nothing about who that is: the whole point of the pending state is that
  // their details are not public until they claim it.
  'recv.pendingHead': 'This card is waiting for its owner',
  'recv.pendingBody': 'Your company set up a card for you. Confirm your work email to take it over — until then, nothing on it is shown.',
  'recv.pendingCta': 'This is my card',
  'recv.invite': 'Invite {name}',

  // --- claiming (the only "sign in" there is) ---
  'claim.askTitle': 'Is this your card?',
  'claim.confirmTitle': 'Just confirming it’s you',
  'claim.sending': 'Sending a code…',
  'claim.sentTo': 'We sent a 6-digit code to {where} — from this card.',
  'claim.autofill': 'It should fill in by itself.',
  'claim.typeIt': 'Enter it and the card is yours.',
  'claim.submit': 'Claim my card',
  'claim.checking': 'Checking…',
  'claim.resend': 'Send a new code',
  // The countdown. Says what the server actually enforces — the window is
  // reported by the claim response, never restated in the client.
  'claim.expiresIn': 'This code expires in {time}',
  'claim.expired': 'This code has expired — send a new one.',
  'claim.resendIn': 'Send a new code in {time}',
  // The other channel. A texted code fills itself in, so the phone is the
  // default — but a default must not be a corner someone is trapped in.
  'claim.useEmail': 'Send it to my email instead',
  'claim.usePhone': 'Text it to my phone instead',
  // Shown INSTEAD of the code box when nothing was sent, never beneath it.
  'claim.cannotBody': 'We couldn’t send a code to this card just now. Try again in a moment.',
  'claim.tryAgain': 'Try again',
  'claim.badCode': 'That code didn’t work. Check it, or send a new one.',
  'claim.cannot': 'This card can’t be claimed right now.',
  'claim.doneHead': 'You’re in.',
  'claim.doneBody': 'This card is yours, and it’ll follow you to any device you open the app on. You won’t be asked again.',
  'claim.done': 'Done',

  // --- editor ---
  'edit.live': 'Your card · Live',
  'edit.fillWith': 'Fill with',
  'edit.voice': 'Voice',
  'edit.ai': 'AI',
  'edit.camera': 'Camera',
  'edit.upload': 'Upload',
  'edit.delete': 'Delete this card',
  'edit.delTitle': 'Delete this card?',
  'edit.delTitleOf': 'Delete {label}?',
  'edit.delBody': 'It stops working everywhere. Any tag or QR code you have handed out will open nothing, and anyone who collected it keeps only what they already saved.',
  'edit.delForever': 'This cannot be undone.',
  'edit.delGo': 'Delete it',
  'edit.delGoing': 'Deleting…',
  'edit.delCancel': 'Keep it',
  'edit.delFailed': 'Could not delete that card. Try again.',
  'edit.delOffline': 'Could not reach the server. Check your connection and try again.',
  'edit.delNeedProof': 'This device cannot prove that card is yours. Sign in with its email or phone number, then delete it.',
  'edit.details': 'Details · only an email is needed',
  'edit.frontHalf': 'Front',
  'edit.toBack': 'Turn the card over',
  'edit.toFront': 'Back to the front',
  'edit.frontHalfNote': 'What someone sees the moment they tap. One of each — only an email is needed.',
  'edit.backHalf': 'Back',
  'edit.backHalfNote': 'Flip side: your pitch, extra numbers and addresses, links and profiles. All optional.',
  'edit.morePhones': 'More numbers',
  'edit.moreEmails': 'More email addresses',
  'edit.socials': 'Profiles',
  'edit.socialsNote': 'Pick a network, paste the link.',
  'edit.addSocial': 'Add a profile',
  'edit.emailWhy': 'Your sign-in code goes here — it’s how you prove this card is yours and use it on another phone.',
  'edit.emailNeeded': 'Add an email address first — it’s how you confirm this card and keep it if you change phones.',
  'edit.dupTitle': 'You already have this card',
  'edit.dupBody': '{label}. A second one would be the same card twice — same company, same role.',
  'edit.dupOpen': 'Open that card instead',
  'edit.dupHint': 'Change the company or the role to make this a different card.',
  'edit.save': 'Save card',
  'edit.saving': 'Saving…',
  'edit.saved': 'Saved',
  'edit.theme': 'Theme',
  'edit.photo': 'Photo',
  'edit.optional': 'optional',
  'edit.change': 'Change',
  'edit.remove': 'Remove',
  'edit.back': 'Back of card',
  'edit.backNote': 'Use a picture instead — a logo, or the artwork already on your printed card.',
  'edit.backHas': 'Your picture is the back of the card.',
  'edit.behind': 'Behind the card · optional',
  'edit.behindNote': 'Shown when someone opens your card. The front stays the same.',
  'edit.addLink': 'Add a link',
  'edit.promo': 'Promo',
  'edit.addPhone': 'Add another number',
  'edit.addEmail': 'Add another email',

  // --- the fill panel (voice / camera / upload) ---
  'fill.close': 'Close',
  'fill.cancel': 'Cancel',
  'fill.capture': 'Capture',
  'fill.describeTitle': 'Describe yourself',
  'fill.describeSub': 'Tell me your role, company, and how to reach you — I’ll draft the card.',
  'fill.startDictation': 'Start voice dictation',
  'fill.stopDictation': 'Stop dictation',
  'fill.listening': 'Listening — speak naturally',
  'fill.tapToSpeak': 'Tap to speak',
  'fill.voiceHint': 'Your words appear in the box as you speak.',
  'fill.voiceNoChrome': '🎤 Voice needs Chrome on Android — type your description below for now.',
  'fill.describePh': 'I’m … , a … at … . Email … , phone … , website … .',
  'fill.build': 'Build my card',
  'fill.building': 'Building…',
  'fill.scanTitle': 'Scan a card',
  'fill.scanSub': 'Snap the card or upload an image — you confirm every field next.',
  'fill.reading': 'Reading the card…',
  'fill.takePhoto': 'Take a photo',
  'fill.takePhotoSub': 'Use your camera now',
  'fill.uploadImage': 'Upload an image',
  'fill.uploadImageSub': 'Pick a photo or file',
  'fill.noCamera': 'Camera unavailable — upload a photo instead.',
  'fill.reviewAi': 'Review · AI draft',
  'fill.reviewScan': 'Review · scanned',
  'fill.nothingSaved': 'Nothing is saved yet — check every detail, then use it.',
  'fill.alsoFound': 'Also printed on this card',
  'fill.extrasNote': 'The front holds one of each, so these go on the back of the card. You can move or remove them after.',
  'fill.pleaseAdd': 'Please add: {fields}.',
  'fill.discard': 'Discard',
  'fill.use': 'Use these details',

  // --- extras, photo, back, theme ---
  'edit.removeLink': 'Remove link',
  'edit.maxLinks': 'That is the maximum of {n} links.',
  'edit.linkLabelPh': 'Label',
  'edit.linkUrlPh': 'example.com/…',
  'edit.promoHeadPh': 'Headline — e.g. 20% off in March',
  'edit.promoBodyPh': 'A line or two about the offer',
  'edit.promoCtaPh': 'Button — e.g. Claim it',
  'edit.promoUrlPh': 'Where it goes',
  'edit.exPhoneLabelPh': 'Mobile',
  'edit.exEmailLabelPh': 'Personal',
  'edit.exPhonePh': '+1 555 0135',
  'edit.exEmailPh': 'you@personal.co',
  'edit.removeNumber': 'Remove this number',
  'edit.removeEmail': 'Remove this email',
  'edit.changePhoto': 'Change your photo',
  'edit.addPhotoOpt': 'Add a photo (optional)',
  'edit.removePhoto': 'Remove photo',
  'edit.designedBack': 'Designed back',
  'edit.themePh': 'e.g. modern vaporwave cyberpunk',
  'edit.speakTheme': 'Speak the theme',
  'edit.generateTheme': 'Generate theme',
  'edit.generating': 'Generating…',

  // --- example values in the empty form ---
  'ex.name': 'Jane Rivera',
  'ex.title': 'Head of Design',
  'ex.company': 'Northwind Studio',
  'ex.monogram': 'JR — initials on the badge',
  'ex.phone': '+1 555 0134',
  'ex.email': 'jane@northwind.co',
  'ex.website': 'northwind.co',
  'ex.address': '500 Market St, San Francisco',
  'ex.tagline': 'Shown on the back — your pitch',

  // --- field labels ---
  'f.name': 'Name',
  'f.title': 'Title',
  'f.company': 'Company',
  'f.monogram': 'Monogram',
  'f.phone': 'Phone',
  'f.email': 'Email',
  'f.website': 'Website',
  'f.address': 'Address',
  'f.tagline': 'Tagline',

  // --- stats ---
  'stats.unit': 'taps all time',
  'stats.unitOne': 'tap all time',
  'stats.window': '{n} in the last {days} days',
  'stats.tapsOn': '{n} taps on {date}',
  'stats.tapsOnOne': '1 tap on {date}',
  'stats.tapsAt': '{n} taps · {date}',
  'stats.tapsAtOne': '1 tap · {date}',
  'stats.none': 'No taps yet — once someone taps your tag or scans the code, it shows up here.',
  'stats.today': 'Today',

  // --- share ---
  'search.clear': 'Clear',
  'search.byVoice': 'Search by voice',
  'search.stopVoice': 'Stop voice search',
  'search.listening': 'Listening…',
  'invite.body': 'Your card is here: {url}',
  'invite.subject': 'Cards',
  'share.title': 'My smart card',
  'share.copy': 'Copy link',
  'share.share': 'Share link',
  'share.copied': 'Link copied!',
  'share.write': 'Write to NFC tag',
  'share.writing': 'Hold a tag to the phone…',

  // --- share / lead card / collect / install / work ---
  'share.nfcOk': 'Tag verified — it opens your card.',
  'share.nfcWritten': 'Tag written. Tap Verify to read it back.',
  'share.nfcFail': 'Couldn’t write the tag. Try again, or share the QR code.',
  'share.print': 'Print this card',
  'share.printing': 'Preparing…',
  'share.printFail': 'Couldn’t load the card for printing. Try again.',
  'share.verify': 'Verify tag',
  'share.nfcHint': 'NFC writing needs Android + Chrome. On this device, share the QR code — anyone can scan it to open your card.',
  'card.connect': 'Let’s connect.',
  'card.defaultTagline': 'Scan to save — or I’ll send it to your phone or email.',
  'card.sendCap': 'Send my card — phone, email, anything',
  'card.verifiedBy': 'Verified by {name}',
  'card.scan': 'Scan',
  'card.flip': 'Flip card',
  'card.verifiedCo': 'Verified company card',
  'card.sendToPh': 'Phone, email, @handle…',
  'card.sendTo': 'Send my card to',
  'card.send': 'Send',

  'col.title': 'Collect a card',
  'col.done': 'Card collected',
  'col.reading': 'Reading…',
  'col.ocr': 'We read it with OCR',
  'col.hold': 'Hold their card to the back of your phone…',
  'col.ready': 'Ready.',
  'col.adding': 'Adding…',
  'col.add': 'Add to collected',
  'col.sub': 'Tap their NFC card, or snap a photo — it’s added to your collected list. No app needed on their side.',
  'col.tapNfc': 'Tap their NFC card',
  'col.tapNfcSub': 'Hold it to your phone',
  'col.photo': 'Take a photo',
  'col.upload': 'Upload an image',
  'col.uploadSub': 'Pick a photo of the card',
  'col.check': 'Check the details, then add them to your collected cards.',
  'col.back': 'Back',
  'col.added': 'Added to your collected cards.',
  'col.another': 'Collect another',

  'ex.also': 'Also reachable at',
  'ex.more': 'More',

  'pwa.update': 'Update available',
  'pwa.updateSub': 'Reload to get the latest version.',
  'pwa.reload': 'Reload',
  'pwa.install': 'Install this app',
  'pwa.installSub': 'Add it to your home screen.',
  'pwa.notNow': 'Not now',
  'pwa.installBtn': 'Install',
  'pwa.a2hs': 'Add to Home Screen',
  'pwa.a2hsSub': 'Tap Share, then “Add to Home Screen”.',
  'pwa.a2hsMenu': 'Open your browser’s menu and choose “Add to Home screen”.',
  'pwa.gotIt': 'Got it',

  'org.pitch': 'Issue cards for your team. Everyone gets their own tag, the company controls what they say, and a card can be reassigned when someone leaves.',
  'org.talkTitle': 'Talk to us',
  'org.talkBody': 'Running this for a larger team, or want terms in writing? Write or call and a person will answer.',
  'org.company': 'Company',
  'org.companyPh': 'Pharmavita Solutions',
  'org.domain': 'Email domain',
  'org.domainPh': 'pharmavita.com',
  'org.verified': '{domain} is verified',
  'org.verifiedSub': 'Cards issued on this domain carry a verified mark, so whoever taps one can tell it really came from {name}.',
  'org.verify': 'Verify {domain}',
  'org.verifySub': 'Add this TXT record to your DNS. Until you do, cards carry no verified mark — anyone could publish a card claiming to be {name}.',
  'org.checkNow': 'I have added it — check now',
  'org.checking': 'Checking…',
  'org.verifiedOk': 'Verified.',
  'org.footer': 'Legal footer — appears on every card',
  'org.footerPh': 'For medical inquiries: medinfo@pharmavita.com',
  'org.office': 'Head office',
  'org.officePh': '100 Innovation Way, Cambridge, MA',
  'org.save': 'Save',
  'org.savedAll': 'Saved to every card.',
  'org.person': 'Person',
  'org.card': 'Card',
  'org.nobody': 'Nobody yet. Add your team above.',
  'org.revoked': 'revoked',
  'org.active': 'active',
  'org.unclaimed': 'unclaimed',
  'org.transfer': 'Hand this card to someone else',
  'org.revoke': 'Take this card out of service',
  'org.syncTitle': 'Sync with your HR system',
  'org.syncSub': 'No connector to install. Generate a key and have Workday, SuccessFactors or any scheduler post your roster — it updates people rather than duplicating them, so you can send the whole list each time.',
  'org.brandTitle': 'Name & logo',
  'org.brandBody': 'The company’s name and mark, as they appear on every card it issues.',
  'org.logoAdd': 'Add a logo',
  'org.logoChange': 'Change logo',
  'org.logoRemove': 'Remove',
  'org.logoAlt': 'Company logo',
  'org.logoHint': 'PNG, JPEG or WebP. It is fitted whole — never cropped — and shown small, so a simple mark works best.',
  'org.brandSaved': 'Saved.',
  'org.sharedTitle': 'Contacts on every card',
  'org.sharedBody': 'Up to three company-wide contacts — a switchboard, a support address — carried by every card the company issues. Employees cannot remove them.',
  'org.sharedLabel': 'Label',
  'org.sharedLabelPh': 'Switchboard',
  'org.sharedValue': 'Phone, email or address',
  'org.sharedValuePh': '+1 555 0100',
  'org.sharedAdd': 'Add a contact',
  'org.sharedRemove': 'Remove this contact',
  'org.syncMode': 'This list is the whole team — revoke anyone missing from it',
  'org.syncModeWarn': 'Anyone the company issued a card to who is not on this list loses it — their tag stops working immediately. Use only with a complete export.',
  'org.syncRevoked': '{n} cards revoked for people no longer on the list:',
  'org.syncRevokedOne': '1 card revoked for someone no longer on the list:',
  'org.department': 'Department',
  'org.personDeptPh': 'Cardiology',
  'org.keyOnce': 'Copy it now — it is stored hashed and cannot be shown again. Generating another retires this one immediately.',
  'org.newKey': 'Generate a new key',
  'org.genKey': 'Generate a key',
  'org.badPaste': 'Nothing to add — check the format.',
  'org.usage': 'How these cards are being used',
  'org.usageNone': 'No taps yet. Cards start counting the first time somebody taps a tag or scans a code.',
  'org.statTaps': 'taps in {days} days',
  'org.statUsed': '{n} of {total} cards tapped at least once',
  'org.statUnclaimed': '{n} not confirmed yet',
  'org.remind': 'Send the link again',
  'org.reminded': 'Sent. They will get a code at their work address.',
  'org.owns': 'What the company owns on every card',
  'org.ownsBody': 'Locked fields are enforced on the server — an employee editing one is refused, not merely greyed out. Everything else stays theirs.',
  'org.fld.name': 'Name',
  'org.fld.title': 'Job title',
  'org.fld.company': 'Company',
  'org.fld.phone': 'Phone',
  'org.fld.email': 'Email',
  'org.fld.photo': 'Photo',
  'org.ownsSaved': 'Saved. It applies to cards created from now on.',
  'org.searchTeam': 'Search {n} people',
  'org.fAll': 'All',
  'org.fActive': 'Active',
  'org.fUnclaimed': 'Unclaimed',
  'org.fRevoked': 'Revoked',
  'org.showMore': 'Show {n} more',
  'org.showingOf': 'Showing {n} of {total}',
  'org.noMatch': 'Nobody matches that.',
  'org.admins': 'Who can manage these cards',
  'org.adminsBody': 'A company outlives the person who set it up. Add a second administrator so leaving does not strand its cards.',
  'org.aloneWarn': 'You are the only administrator. If you leave, nobody can revoke a card, hand one over, or change these settings.',
  'org.addAdmin': 'Add administrator',
  'org.adminEmail': 'Their work email',
  'org.you': 'you',
  'org.removeAdmin': 'Remove {name}',
  'org.stepDown': 'Step down',
  'org.link': 'Card link',
  'org.copyLink': 'Copy link',
  'org.copied2': 'Copied',
  'org.download': 'Download links (CSV)',
  'org.downloadHint': 'Every card’s link, for writing tags or handing out.',
  'org.printAll': 'Print all cards',
  'org.printAllHint': 'Every active card’s face, one per page, at physical size (85×55mm).',
  'org.transferTitle': 'Hand {name}’s card to someone else',
  'org.transferBody': 'The tag keeps working — only the person behind it changes.',
  'org.transferTo': 'Their work email',
  'org.transferGo': 'Hand it over',
  'org.revokeTitle': 'Take {name}’s card out of service?',
  'org.revokeBody': 'Their tag stops working. A tap will look exactly like a card that never existed.',
  'org.revokeGo': 'Take it out of service',
  'org.cancel': 'Cancel',
  'org.setUp': 'Set up company cards',
  'org.details': 'Company details & integrations',
  'org.step1': 'Company',
  'org.step2': 'Verify',
  'org.step3': 'Your team',
  'org.stepOf': 'Step {n} of 3',
  'org.copy': 'Copy',
  'org.copied': 'Copied',
  'org.dnsHost': 'Name / host',
  'org.dnsValue': 'Value',
  'org.dnsWhere': 'Wherever {domain}’s DNS is managed. Changes usually take a few minutes to show up.',
  'org.willSkip': '{n} will be skipped — the server only issues cards on {domain}.',
  'org.willSkipOne': '1 will be skipped — the server only issues cards on {domain}.',
  'org.laterNote': 'Cards work either way — until the domain is verified they simply carry no verified mark.',
  'org.willCreate': 'Will create {n} cards',
  'org.willCreateOne': 'Will create 1 card',
  'org.nothingYet': 'Paste your team above and they will appear here first.',
  'org.previewTitle': 'About to be created',
  'org.offDomain': 'not on {domain}',
  'org.teamCount': '{n} people',
  'org.teamCountOne': '1 person',
  'org.domainHint': 'Employees confirm their card with a code sent here.',
  'org.auditTitle': 'Activity log',
  'org.auditSub': 'What administrators did, newest first. Recorded automatically and cannot be edited.',
  'org.auditEmpty': 'Nothing yet.',
  'org.auditByKey': 'via API key',
  'org.auditBySystem': 'system',
  'org.auditUnknown': 'Action',
  'org.audit.org.created': 'Company created',
  'org.audit.org.updated': 'Company details changed',
  'org.audit.org.domain_verified': 'Domain verified',
  'org.audit.org.key_issued': 'API key issued',
  'org.audit.roster.imported': 'People imported',
  'org.audit.card.revoked': 'Card revoked',
  'org.audit.card.transferred': 'Card transferred',
  'org.audit.card.reminded': 'Reminder sent',
  'org.audit.admin.added': 'Administrator added',
  'org.audit.admin.removed': 'Administrator removed',
  'org.audit.event.created': 'Event created',
  'org.audit.event.updated': 'Event changed',
  'org.audit.event.deleted': 'Event deleted',
  'org.audit.event.ended': 'Event ended',
  'org.audit.event.attendance_read': 'Attendance exported',
  'org.switchLabel': 'Which company',
  'org.employees': 'Employees',
  'org.addPeople': 'Add people',
  'org.uploadCsv': 'Upload a CSV',
  'org.orPaste': 'or paste it below',
  'org.dropHint': 'CSV or TSV — straight out of your HR system, Excel or Google Sheets. Column order does not matter if the file has a header row.',
  'org.addOne': 'Type in one person instead',
  'org.backToBulk': 'Import a list instead',
  'org.headerFound': 'Using the first row as column headings.',
  'org.badFile': 'Could not read that file.',
  'org.skippedLines': '{n} lines could not be used',
  'org.skippedLinesOne': '1 line could not be used',
  'org.skipNoEmail': 'no email',
  'org.skipNoName': 'no name',
  'org.line': 'Line {n}',
  'org.addPerson': 'Add another person',
  'org.removePerson': 'Remove this person',
  'org.workEmail': 'Work email',
  'org.jobTitle': 'Job title',
  'org.optional': 'optional',
  'org.personNamePh': 'Jane Doe',
  'org.personEmailPh': 'jane.doe@pharmavita.com',
  'org.personTitlePh': 'Medical Science Liaison',
  'org.personPhonePh': '+1 555 0134',
  'org.pasteSub': 'Each person gets an unclaimed card. They take it over with a code sent to their work address{on} — nothing to remember, and nothing for you to distribute.',
  'org.onDomain': ' on {domain}',
  'org.creating': 'Creating…',
  'org.createCards': 'Create their cards',
  'org.cardsCreated': 'Cards created.',
  'org.cardRevoked': 'Card revoked.',
  'org.cardTransferred': 'Card transferred.',
  'org.transferAria': 'Transfer {name}’s card',
  'org.revokeAria': 'Revoke {name}’s card',
  'org.confirmRevoke': 'Take {name}’s card out of service? Their tag will stop working.',
  'org.promptTransfer': 'Hand {name}’s card to which work address?\n\nThe tag keeps working — only the person behind it changes.',
  'org.skippedLine': 'Line {row} ({email}): {reason}',

  // --- language ---
  'lang.title': 'Language',
} as const;

export type MessageKey = keyof typeof en;

/** Bangla. Written to read naturally rather than word-for-word — a literal
 *  translation of English UI copy reads like a manual in any language. */
const bn: Record<MessageKey, string> = {
  'app.title': 'কার্ড',
  'nav.work': 'প্রতিষ্ঠান',
  'nav.home': 'আমার কার্ড',
  // Mirrors the English pair exactly: "আমার কার্ড" (my cards) against plain
  // "কার্ড" (cards), so the two controls stay distinguishable at a glance.
  'nav.cards': 'কার্ড',
  'mine.delete': 'মুছুন',
  'mine.deleted': '{label} মুছে ফেলা হয়েছে।',
  'mine.many': 'আপনার কার্ডগুলো',
  'mine.edit': 'এই কার্ডটি সম্পাদনা করুন',
  'mine.create': 'নতুন কার্ড',
  'mine.deck': 'আপনার কার্ডগুলো',
  'mine.nOfTotal': '{total}টির মধ্যে {n} নম্বর',
  'mine.goToCard': '{name} দেখুন',
  'mine.loading': 'লোড হচ্ছে…',
  'dir.title': 'সংগৃহীত',
  'dir.collect': 'সংগ্রহ',
  'dir.search': '{n}টি কার্ডে খুঁজুন',
  'dir.searchOne': '১টি কার্ডে খুঁজুন',
  'dir.all': 'সব',
  'dir.empty': 'এখনও কোনো কার্ড নেই — কারও কার্ডে ট্যাপ করে সংগ্রহ করুন।',
  'dir.export': 'রপ্তানি',
  'dir.exportHint': 'দেখানো পরিচিতিগুলো সিএসভি ফাইলে নামান',
  'dir.noteAdd': 'নোট যোগ করুন',
  'dir.noteSave': 'সংরক্ষণ',
  'dir.noteCancel': 'বাতিল',
  'dir.notePlaceholder': 'কোথায় দেখা হলো, পরে কী করতে হবে — এটি কেবল আপনিই দেখতে পাবেন।',
  'dir.noteAria': '{name} সম্পর্কে আপনার ব্যক্তিগত নোট',
  'dir.tagPlaceholder': 'ট্যাগ, কমা দিয়ে আলাদা করুন — মেলা, পরে যোগাযোগ',
  'dir.tagAria': '{name}-এর জন্য আপনার ট্যাগ',
  'dir.tagFilterLabel': 'আপনার ট্যাগ দিয়ে ছাঁকুন',
  'dir.tagFilterHint': 'কেবল {tag} দেখান',
  'dir.tagAll': 'সব',
  'dir.noMatch': 'এর সাথে কিছু মিলল না।',
  'work.title': 'প্রতিষ্ঠান',
  'org.makeCardFirst': 'শুরু করুন — আমার কার্ড বানাই',
  'org.claimCardFirst': 'শুরু করুন — আমার কার্ড নিশ্চিত করি',
  'org.needIdentity': 'আগে নিজের কার্ডটি বানিয়ে কোড দিয়ে নিশ্চিত করুন। প্রতিষ্ঠান পরিচালনা করেন একজন মানুষ, তাই আগে সেই পরিচয়টি থাকা দরকার।',

  'recv.shared': 'শেয়ার করা কার্ড',
  'recv.saved': 'আপনার কার্ডে সংরক্ষিত',
  'recv.seeAll': 'সবগুলো দেখুন ({n})',
  'recv.saveContact': 'ফোনের কন্টাক্টে সংরক্ষণ করুন',
  'recv.tapHint': 'কার্ডের যেকোনো লাইনে চাপ দিলে কল, ইমেইল বা লিংক খুলবে।',
  'recv.savedContact': 'কন্টাক্টে যোগ হয়েছে',
  'recv.mine': 'এটি আমার কার্ড',
  'recv.noApp': 'কোনো অ্যাপ লাগে না — ট্যাপ করলেই খুলে যায়।',
  'recv.report': 'এই কার্ডটি রিপোর্ট করুন',
  'card.guest': 'অতিথি',
  'ex.social': 'প্রোফাইল',
  'dir.tools': 'মেনু',
  'dir.notes': 'নোট',
  'notes.take': 'নোট লিখুন…',
  'notes.ph': 'যা খুশি লিখুন — লেখার সাথে সাথেই সংরক্ষিত হয়, এবং এই ডিভাইসেই থাকে।',
  'notes.empty': 'এখনো কোনো নোট নেই।',
  'notes.noMatch': 'এমন কোনো নোট নেই।',
  'notes.search': 'নোট খুঁজুন',
  'notes.delete': 'মুছুন',
  'notes.share': 'শেয়ার',
  'notes.copied': 'কপি হয়েছে',
  'edit.useExisting': 'বিদ্যমান ছবি ব্যবহার করুন',
  'org.events': 'ইভেন্ট',
  'org.evSub': 'যারা কর্মী নন তাদের জন্য ব্যাজ — মেলার অতিথি, প্রশিক্ষণের অংশগ্রহণকারী। এগুলো কার্ডের মতোই কাজ করে, কখনো আপনার যাচাইকৃত চিহ্ন বহন করে না, এবং ইভেন্ট শেষ হলে বন্ধ হয়ে যায়।',
  'org.evNew': 'নতুন ইভেন্ট',
  'org.evName': 'ইভেন্টের নাম',
  'org.evStart': 'শুরু',
  'org.evEnd': 'শেষ',
  'org.evCreate': 'তৈরি করুন',
  'org.evNone': 'এখনো কোনো ইভেন্ট নেই।',
  'org.evEnded': 'শেষ হয়েছে',
  'org.evAttendees': '{n} জন অংশগ্রহণকারী',
  'org.evCounts': '{people} জন অংশগ্রহণকারী · {here} জন উপস্থিত',
  'org.evPastePh': 'অংশগ্রহণকারীদের পেস্ট করুন — এখানে যেকোনো ইমেইল চলবে: প্রতি লাইনে নাম, ইমেইল, বা স্প্রেডশিট থেকে সেল',
  'org.evImport': 'অংশগ্রহণকারী যোগ করুন',
  'org.evImported': '{n}টি ব্যাজ প্রস্তুত',
  'org.evPrint': 'ব্যাজ প্রিন্ট করুন',
  'org.evScan': 'ব্যাজ স্ক্যান করুন',
  'org.evStopScan': 'স্ক্যান বন্ধ করুন',
  'org.evManualPh': 'ব্যাজ কোড (QR-এর নিচে)',
  'org.evCheckin': 'চেক ইন',
  'org.evWelcome': 'স্বাগতম, {name}',
  'org.evWelcomeBack': 'আবার স্বাগতম, {name}',
  'org.evDeduped': '{name} ইতিমধ্যে চেক ইন করেছেন',
  'org.evCamera': 'ক্যামেরা চালু হয়নি — ব্যাজ কোডটি টাইপ করুন।',
  'org.evEndNow': 'ইভেন্ট শেষ করুন',
  'org.evEndTitle': 'এখনই “{name}” শেষ করবেন?',
  'org.evEndBody': 'সব ব্যাজ সঙ্গে সঙ্গে বন্ধ হয়ে যাবে, এবং এটি আর ফেরানো যাবে না।',
  'org.evEndGo': 'এখনই শেষ করুন',
  'org.evEdit': 'সম্পাদনা',
  'org.evSave': 'পরিবর্তন সংরক্ষণ',
  'org.evUpdated': 'ইভেন্ট হালনাগাদ হয়েছে।',
  'org.evDelete': 'মুছে ফেলুন',
  'org.evDeleteTitle': '“{name}” মুছে ফেলবেন?',
  'org.evDeleteBody': 'এটি কোনো ব্যাজ ইস্যু করেনি, তাই রাখার কিছু নেই। এটি আর ফেরানো যাবে না।',
  'org.evDeleteGo': 'মুছে ফেলুন',
  'org.evExport': 'উপস্থিতির CSV',
  'recv.checkinNote': 'এই ব্যাজটি {event}-এ আপনার উপস্থিতি নথিভুক্ত করে।',
  'report.title': 'এই কার্ডটি রিপোর্ট করুন',
  'report.sub': 'আপনার সম্পর্কে কিছুই পাঠানো হয় না — শুধু কোন কার্ড এবং কেন।',
  'report.r.spam': 'স্প্যাম',
  'report.r.abuse': 'আপত্তিকর বিষয়বস্তু',
  'report.r.impersonation': 'পরিচয় জালিয়াতি',
  'report.r.illegal': 'বেআইনি বিষয়বস্তু',
  'report.r.other': 'অন্য কিছু',
  'report.detailsPh': 'খতিয়ে দেখতে সাহায্য করে এমন কিছু (ঐচ্ছিক)',
  'report.send': 'রিপোর্ট পাঠান',
  'report.sending': 'পাঠানো হচ্ছে…',
  'report.cancel': 'বাতিল',
  'report.failed': 'এই মুহূর্তে পাঠানো গেল না — আবার চেষ্টা করুন।',
  'report.thanks': 'ধন্যবাদ। আমরা দেখে নেব।',
  'report.close': 'বন্ধ করুন',
  'legal.partOf': '{parent}-এর অংশ',
  'legal.privacy': 'গোপনীয়তা',
  'legal.terms': 'শর্তাবলি',
  'legal.notice': 'আইনি তথ্য',
  'legal.contact': 'যোগাযোগ',
  'recv.pitchCta': 'আমার কার্ড বানাই',
  'recv.keepHead': 'আপনার কার্ডগুলো রাখুন',
  'recv.keepHint': 'আপনার নম্বর দিন — সংগ্রহ করা প্রতিটি কার্ড যেকোনো ডিভাইসে সঙ্গে থাকবে।',
  'recv.keepPh': 'আপনার নম্বর',
  'recv.keepCta': 'কোড পাঠান',
  'recv.keepSent': '{where}-এ একটি কোড পাঠানো হয়েছে।',
  'recv.keepConfirm': 'নিশ্চিত করুন',
  'recv.keepFail': 'সঠিক নম্বর বা ঠিকানা দিন।',
  'recv.yourCards': 'আপনার সংগ্রহ',
  'recv.notFound': 'কার্ড পাওয়া যায়নি, অথবা আপনি অফলাইনে আছেন।',
  'recv.pendingHead': 'এই কার্ডটি তার মালিকের অপেক্ষায়',
  'recv.pendingBody': 'আপনার প্রতিষ্ঠান আপনার জন্য একটি কার্ড তৈরি করেছে। এটি নিতে আপনার অফিসের ইমেইল নিশ্চিত করুন — ততক্ষণ পর্যন্ত এতে কিছুই দেখানো হবে না।',
  'recv.pendingCta': 'এটি আমার কার্ড',
  'recv.invite': '{name}-কে আমন্ত্রণ জানান',

  'claim.askTitle': 'এটি কি আপনার কার্ড?',
  'claim.confirmTitle': 'শুধু নিশ্চিত করছি এটি আপনিই',
  'claim.sending': 'কোড পাঠানো হচ্ছে…',
  'claim.sentTo': 'এই কার্ডের {where}-এ ৬ সংখ্যার একটি কোড পাঠানো হয়েছে।',
  'claim.autofill': 'কোডটি নিজে থেকেই বসে যাওয়ার কথা।',
  'claim.typeIt': 'কোডটি লিখলেই কার্ডটি আপনার।',
  'claim.submit': 'কার্ডটি আমার',
  'claim.checking': 'যাচাই করা হচ্ছে…',
  'claim.resend': 'নতুন কোড পাঠান',
  'claim.expiresIn': 'কোডটির মেয়াদ শেষ হবে {time} পরে',
  'claim.expired': 'কোডটির মেয়াদ শেষ — নতুন কোড নিন।',
  'claim.resendIn': '{time} পরে নতুন কোড পাঠান',
  'claim.useEmail': 'বদলে আমার ইমেইলে পাঠান',
  'claim.usePhone': 'বদলে আমার ফোনে এসএমএস করুন',
  'claim.cannotBody': 'এই মুহূর্তে কার্ডটিতে কোড পাঠানো গেল না। একটু পরে আবার চেষ্টা করুন।',
  'claim.tryAgain': 'আবার চেষ্টা করুন',
  'claim.badCode': 'কোডটি মিলল না। আবার দেখুন, অথবা নতুন কোড নিন।',
  'claim.cannot': 'এই কার্ডটি এখন দাবি করা যাচ্ছে না।',
  'claim.doneHead': 'হয়ে গেছে।',
  'claim.doneBody': 'কার্ডটি এখন আপনার, এবং যে ডিভাইসেই অ্যাপটি খুলবেন সেখানেই পাবেন। আর জিজ্ঞেস করা হবে না।',
  'claim.done': 'সম্পন্ন',

  'edit.live': 'আপনার কার্ড · সরাসরি',
  'edit.fillWith': 'যেভাবে পূরণ করবেন',
  'edit.voice': 'কণ্ঠস্বর',
  'edit.ai': 'এআই',
  'edit.camera': 'ক্যামেরা',
  'edit.upload': 'আপলোড',
  'edit.delete': 'এই কার্ডটি মুছুন',
  'edit.delTitle': 'কার্ডটি মুছে ফেলবেন?',
  'edit.delTitleOf': '{label} মুছে ফেলবেন?',
  'edit.delBody': 'এটি সব জায়গায় কাজ করা বন্ধ করবে। আপনি যে ট্যাগ বা কিউআর কোড দিয়েছেন সেগুলো আর কিছু খুলবে না, আর যাঁরা সংগ্রহ করেছেন তাঁদের কাছে আগে সংরক্ষিত তথ্যটুকুই থাকবে।',
  'edit.delForever': 'এটি আর ফেরানো যাবে না।',
  'edit.delGo': 'মুছে ফেলুন',
  'edit.delGoing': 'মোছা হচ্ছে…',
  'edit.delCancel': 'থাক',
  'edit.delFailed': 'কার্ডটি মোছা গেল না। আবার চেষ্টা করুন।',
  'edit.delOffline': 'সার্ভারে পৌঁছানো গেল না। সংযোগ দেখে আবার চেষ্টা করুন।',
  'edit.delNeedProof': 'এই ডিভাইস প্রমাণ করতে পারছে না যে কার্ডটি আপনার। কার্ডের ইমেইল বা ফোন নম্বর দিয়ে সাইন ইন করে মুছুন।',
  'edit.details': 'বিবরণ · শুধু একটি ইমেইল দরকার',
  'edit.frontHalf': 'সামনে',
  'edit.toBack': 'কার্ডটি উল্টান',
  'edit.toFront': 'সামনে ফিরুন',
  'edit.frontHalfNote': 'ট্যাপ করার সঙ্গে সঙ্গে যা দেখা যায়। প্রতিটির একটি করে — শুধু একটি ইমেইল দরকার।',
  'edit.backHalf': 'পেছনে',
  'edit.backHalfNote': 'উল্টো পিঠ: আপনার পরিচিতি, বাড়তি নম্বর ও ঠিকানা, লিংক ও প্রোফাইল। সবই ঐচ্ছিক।',
  'edit.morePhones': 'আরও নম্বর',
  'edit.moreEmails': 'আরও ইমেইল ঠিকানা',
  'edit.socials': 'প্রোফাইল',
  'edit.socialsNote': 'নেটওয়ার্ক বেছে নিন, লিংক দিন।',
  'edit.addSocial': 'প্রোফাইল যোগ করুন',
  'edit.emailWhy': 'আপনার সাইন-ইন কোড এখানে যাবে — এটি দিয়েই প্রমাণ হয় কার্ডটি আপনার, আর অন্য ফোনেও ব্যবহার করা যায়।',
  'edit.emailNeeded': 'আগে একটি ইমেইল ঠিকানা দিন — এটি দিয়েই কার্ডটি নিশ্চিত করবেন এবং ফোন বদলালেও কার্ডটি থাকবে।',
  'edit.dupTitle': 'এই কার্ডটি আপনার আগে থেকেই আছে',
  'edit.dupBody': '{label}। আরেকটি বানালে একই কার্ড দুবার হয়ে যাবে — একই প্রতিষ্ঠান, একই পদ।',
  'edit.dupOpen': 'বরং সেই কার্ডটি খুলুন',
  'edit.dupHint': 'আলাদা কার্ড বানাতে প্রতিষ্ঠান বা পদবি বদলে নিন।',
  'edit.save': 'কার্ড সংরক্ষণ',
  'edit.saving': 'সংরক্ষণ হচ্ছে…',
  'edit.saved': 'সংরক্ষিত',
  'edit.theme': 'থিম',
  'edit.photo': 'ছবি',
  'edit.optional': 'ঐচ্ছিক',
  'edit.change': 'বদলান',
  'edit.remove': 'সরান',
  'edit.back': 'কার্ডের পেছনের দিক',
  'edit.backNote': 'চাইলে ছবি ব্যবহার করুন — লোগো, বা ছাপানো কার্ডে যে নকশা আছে সেটি।',
  'edit.backHas': 'আপনার ছবিটিই কার্ডের পেছনের দিক।',
  'edit.behind': 'কার্ডের পেছনে · ঐচ্ছিক',
  'edit.behindNote': 'কেউ আপনার কার্ড খুললে এগুলো দেখা যাবে। সামনের দিক অপরিবর্তিত থাকে।',
  'edit.addLink': 'লিংক যোগ করুন',
  'edit.promo': 'প্রচার',
  'edit.addPhone': 'আরেকটি নম্বর যোগ করুন',
  'edit.addEmail': 'আরেকটি ইমেইল যোগ করুন',

  'fill.close': 'বন্ধ করুন',
  'fill.cancel': 'বাতিল',
  'fill.capture': 'ছবি তুলুন',
  'fill.describeTitle': 'নিজের পরিচয় দিন',
  'fill.describeSub': 'আপনার পদ, প্রতিষ্ঠান আর যোগাযোগের উপায় বলুন — আমি কার্ডের খসড়া করে দিচ্ছি।',
  'fill.startDictation': 'বলা শুরু করুন',
  'fill.stopDictation': 'বলা বন্ধ করুন',
  'fill.listening': 'শুনছি — স্বাভাবিকভাবে বলুন',
  'fill.tapToSpeak': 'বলতে ট্যাপ করুন',
  'fill.voiceHint': 'আপনি বলার সাথে সাথে কথাগুলো বাক্সে দেখা যাবে।',
  'fill.voiceNoChrome': '🎤 ভয়েসের জন্য অ্যান্ড্রয়েডে ক্রোম দরকার — আপাতত নিচে টাইপ করে বর্ণনা দিন।',
  'fill.describePh': 'আমি … , … প্রতিষ্ঠানের … । ইমেইল … , ফোন … , ওয়েবসাইট … ।',
  'fill.build': 'আমার কার্ড বানান',
  'fill.building': 'বানানো হচ্ছে…',
  'fill.scanTitle': 'কার্ড স্ক্যান করুন',
  'fill.scanSub': 'কার্ডের ছবি তুলুন বা আপলোড করুন — এরপর প্রতিটি তথ্য আপনি মিলিয়ে নেবেন।',
  'fill.reading': 'কার্ড পড়া হচ্ছে…',
  'fill.takePhoto': 'ছবি তুলুন',
  'fill.takePhotoSub': 'এখনই ক্যামেরা ব্যবহার করুন',
  'fill.uploadImage': 'ছবি আপলোড করুন',
  'fill.uploadImageSub': 'একটি ছবি বা ফাইল বাছুন',
  'fill.noCamera': 'ক্যামেরা পাওয়া যাচ্ছে না — এর বদলে ছবি আপলোড করুন।',
  'fill.reviewAi': 'যাচাই · এআই খসড়া',
  'fill.reviewScan': 'যাচাই · স্ক্যান করা',
  'fill.nothingSaved': 'এখনও কিছু সংরক্ষণ হয়নি — সব তথ্য দেখে নিয়ে তারপর ব্যবহার করুন।',
  'fill.alsoFound': 'এই কার্ডে আরও ছাপা আছে',
  'fill.extrasNote': 'কার্ডের সামনে প্রতিটির একটি করে জায়গা, তাই এগুলো কার্ডের পেছনে যাবে। পরে সরানো বা মুছে ফেলা যাবে।',
  'fill.pleaseAdd': 'যোগ করুন: {fields}।',
  'fill.discard': 'বাদ দিন',
  'fill.use': 'এই তথ্যগুলো ব্যবহার করুন',

  'edit.removeLink': 'লিংক সরান',
  'edit.maxLinks': 'সর্বোচ্চ {n}টি লিংক দেওয়া যায়।',
  'edit.linkLabelPh': 'লেবেল',
  'edit.linkUrlPh': 'example.com/…',
  'edit.promoHeadPh': 'শিরোনাম — যেমন মার্চে ২০% ছাড়',
  'edit.promoBodyPh': 'অফারটি নিয়ে দু-এক লাইন',
  'edit.promoCtaPh': 'বোতাম — যেমন নিয়ে নিন',
  'edit.promoUrlPh': 'কোথায় যাবে',
  'edit.exPhoneLabelPh': 'মোবাইল',
  'edit.exEmailLabelPh': 'ব্যক্তিগত',
  'edit.exPhonePh': '+880 1712 345679',
  'edit.exEmailPh': 'you@personal.co',
  'edit.removeNumber': 'এই নম্বরটি সরান',
  'edit.removeEmail': 'এই ইমেইলটি সরান',
  'edit.changePhoto': 'ছবি বদলান',
  'edit.addPhotoOpt': 'ছবি যোগ করুন (ঐচ্ছিক)',
  'edit.removePhoto': 'ছবি সরান',
  'edit.designedBack': 'নকশা করা পেছন',
  'edit.themePh': 'যেমন আধুনিক ভেপারওয়েভ সাইবারপাংক',
  'edit.speakTheme': 'থিমটি বলুন',
  'edit.generateTheme': 'থিম তৈরি করুন',
  'edit.generating': 'তৈরি হচ্ছে…',

  // Example values, localised rather than transliterated: a Bangla speaker
  // filling this form is far likelier to have a +880 number and a .com.bd
  // address, and a placeholder that looks like their own data reads as an
  // example instead of as something already filled in.
  'ex.name': 'নুসরাত রহমান',
  'ex.title': 'ডিজাইন প্রধান',
  'ex.company': 'নর্থউইন্ড স্টুডিও',
  'ex.monogram': 'নর — ব্যাজে যে আদ্যক্ষর বসবে',
  'ex.phone': '+880 1712 345678',
  'ex.email': 'nusrat@northwind.com.bd',
  'ex.website': 'northwind.com.bd',
  'ex.address': '১২ কামাল আতাতুর্ক অ্যাভিনিউ, বনানী, ঢাকা',
  'ex.tagline': 'কার্ডের পেছনে দেখা যাবে — আপনার কথাটি',

  'f.name': 'নাম',
  'f.title': 'পদবি',
  'f.company': 'প্রতিষ্ঠান',
  'f.monogram': 'মনোগ্রাম',
  'f.phone': 'ফোন',
  'f.email': 'ইমেইল',
  'f.website': 'ওয়েবসাইট',
  'f.address': 'ঠিকানা',
  'f.tagline': 'ট্যাগলাইন',
  'stats.unit': 'মোট ট্যাপ',
  'stats.unitOne': 'মোট ট্যাপ',
  'stats.window': 'গত {days} দিনে {n}',
  'stats.tapsOn': '{date} তারিখে {n} ট্যাপ',
  'stats.tapsOnOne': '{date} তারিখে ১ ট্যাপ',
  'stats.tapsAt': '{n} ট্যাপ · {date}',
  'stats.tapsAtOne': '১ ট্যাপ · {date}',
  'stats.none': 'এখনও কোনো ট্যাপ হয়নি — কেউ আপনার ট্যাগে ট্যাপ করলে বা কোড স্ক্যান করলে এখানে দেখা যাবে।',
  'stats.today': 'আজ',

  'search.clear': 'মুছুন',
  'search.byVoice': 'কণ্ঠে খুঁজুন',
  'search.stopVoice': 'কণ্ঠে খোঁজা বন্ধ করুন',
  'search.listening': 'শুনছি…',
  'invite.body': 'আপনার কার্ড এখানে: {url}',
  'invite.subject': 'কার্ড',
  'share.title': 'আমার স্মার্ট কার্ড',
  'share.copy': 'লিংক কপি করুন',
  'share.share': 'লিংক শেয়ার করুন',
  'share.copied': 'লিংক কপি হয়েছে!',
  'share.write': 'এনএফসি ট্যাগে লিখুন',
  'share.writing': 'ফোনে একটি ট্যাগ ধরুন…',

  'share.nfcOk': 'ট্যাগ যাচাই হয়েছে — এটি আপনার কার্ড খোলে।',
  'share.nfcWritten': 'ট্যাগে লেখা হয়েছে। পড়ে দেখতে “ট্যাগ যাচাই” চাপুন।',
  'share.nfcFail': 'ট্যাগে লেখা গেল না। আবার চেষ্টা করুন, অথবা কিউআর কোডটি শেয়ার করুন।',
  'share.print': 'এই কার্ডটি প্রিন্ট করুন',
  'share.printing': 'প্রস্তুত হচ্ছে…',
  'share.printFail': 'প্রিন্টের জন্য কার্ডটি আনা গেল না। আবার চেষ্টা করুন।',
  'share.verify': 'ট্যাগ যাচাই',
  'share.nfcHint': 'এনএফসি লেখার জন্য অ্যান্ড্রয়েড ও ক্রোম দরকার। এই ডিভাইসে কিউআর কোডটি শেয়ার করুন — যে কেউ স্ক্যান করে আপনার কার্ড খুলতে পারবে।',
  'card.connect': 'চলুন যোগাযোগ করি।',
  'card.defaultTagline': 'সংরক্ষণ করতে স্ক্যান করুন — অথবা আমি আপনার ফোনে বা ইমেইলে পাঠিয়ে দিই।',
  'card.sendCap': 'আমার কার্ড পাঠাই — ফোন, ইমেইল, যেকোনো কিছুতে',
  'card.verifiedBy': '{name} যাচাই করেছে',
  'card.scan': 'স্ক্যান',
  'card.flip': 'কার্ড ওল্টান',
  'card.verifiedCo': 'যাচাই করা প্রাতিষ্ঠানিক কার্ড',
  'card.sendToPh': 'ফোন, ইমেইল, @হ্যান্ডেল…',
  'card.sendTo': 'আমার কার্ড পাঠান',
  'card.send': 'পাঠান',

  'col.title': 'কার্ড সংগ্রহ করুন',
  'col.done': 'কার্ড সংগৃহীত',
  'col.reading': 'পড়া হচ্ছে…',
  'col.ocr': 'আমরা ওসিআর দিয়ে পড়ে নিই',
  'col.hold': 'তাঁদের কার্ডটি ফোনের পেছনে ধরে রাখুন…',
  'col.ready': 'প্রস্তুত।',
  'col.adding': 'যোগ করা হচ্ছে…',
  'col.add': 'সংগ্রহে যোগ করুন',
  'col.sub': 'তাঁদের এনএফসি কার্ডে ট্যাপ করুন, বা ছবি তুলুন — সেটি আপনার সংগ্রহে যুক্ত হবে। তাঁদের কোনো অ্যাপ লাগবে না।',
  'col.tapNfc': 'তাঁদের এনএফসি কার্ডে ট্যাপ করুন',
  'col.tapNfcSub': 'ফোনের কাছে ধরুন',
  'col.photo': 'ছবি তুলুন',
  'col.upload': 'ছবি আপলোড করুন',
  'col.uploadSub': 'কার্ডের একটি ছবি বাছুন',
  'col.check': 'তথ্যগুলো দেখে নিয়ে সংগ্রহে যোগ করুন।',
  'col.back': 'পেছনে',
  'col.added': 'আপনার সংগ্রহে যুক্ত হয়েছে।',
  'col.another': 'আরেকটি সংগ্রহ করুন',

  'ex.also': 'আরও যেভাবে পাওয়া যাবে',
  'ex.more': 'আরও',

  'pwa.update': 'নতুন সংস্করণ এসেছে',
  'pwa.updateSub': 'সর্বশেষটি পেতে রিলোড করুন।',
  'pwa.reload': 'রিলোড',
  'pwa.install': 'অ্যাপটি ইনস্টল করুন',
  'pwa.installSub': 'হোম স্ক্রিনে যোগ করুন।',
  'pwa.notNow': 'এখন নয়',
  'pwa.installBtn': 'ইনস্টল',
  'pwa.a2hs': 'হোম স্ক্রিনে যোগ করুন',
  'pwa.a2hsSub': 'শেয়ার চাপুন, তারপর “Add to Home Screen”।',
  'pwa.a2hsMenu': 'ব্রাউজারের মেনু খুলে “হোম স্ক্রিনে যোগ করুন” বেছে নিন।',
  'pwa.gotIt': 'বুঝেছি',

  'org.pitch': 'আপনার দলের জন্য কার্ড দিন। প্রত্যেকে নিজের ট্যাগ পাবেন, কার্ডে কী থাকবে তা প্রতিষ্ঠান ঠিক করবে, আর কেউ চলে গেলে কার্ডটি অন্যকে দেওয়া যাবে।',
  'org.talkTitle': 'আমাদের সঙ্গে কথা বলুন',
  'org.talkBody': 'বড় দলের জন্য ব্যবহার করছেন, নাকি লিখিত চুক্তি চান? লিখুন বা ফোন করুন — একজন মানুষ উত্তর দেবেন।',
  'org.company': 'প্রতিষ্ঠান',
  'org.companyPh': 'ফার্মাভিটা সলিউশনস',
  'org.domain': 'ইমেইল ডোমেইন',
  'org.domainPh': 'pharmavita.com',
  'org.verified': '{domain} যাচাই করা হয়েছে',
  'org.verifiedSub': 'এই ডোমেইনে দেওয়া কার্ডে যাচাইয়ের চিহ্ন থাকে, তাই যিনি ট্যাপ করবেন তিনি বুঝবেন এটি সত্যিই {name} থেকেই এসেছে।',
  'org.verify': '{domain} যাচাই করুন',
  'org.verifySub': 'আপনার ডিএনএস-এ এই TXT রেকর্ডটি যোগ করুন। তার আগ পর্যন্ত কার্ডে যাচাইয়ের চিহ্ন থাকবে না — যে কেউ {name} সেজে কার্ড প্রকাশ করতে পারে।',
  'org.checkNow': 'যোগ করেছি — এখন দেখুন',
  'org.checking': 'দেখা হচ্ছে…',
  'org.verifiedOk': 'যাচাই সম্পন্ন।',
  'org.footer': 'আইনি ফুটার — প্রতিটি কার্ডে দেখা যাবে',
  'org.footerPh': 'চিকিৎসা-সংক্রান্ত জিজ্ঞাসায়: medinfo@pharmavita.com',
  'org.office': 'প্রধান কার্যালয়',
  'org.officePh': '১০০ ইনোভেশন ওয়ে, কেমব্রিজ, এমএ',
  'org.save': 'সংরক্ষণ',
  'org.savedAll': 'প্রতিটি কার্ডে সংরক্ষিত হয়েছে।',
  'org.person': 'ব্যক্তি',
  'org.card': 'কার্ড',
  'org.nobody': 'এখনও কেউ নেই। উপরে আপনার দলকে যোগ করুন।',
  'org.revoked': 'বাতিল',
  'org.active': 'সচল',
  'org.unclaimed': 'অদাবিকৃত',
  'org.transfer': 'কার্ডটি অন্য কাউকে দিন',
  'org.revoke': 'কার্ডটি বাতিল করুন',
  'org.syncTitle': 'আপনার এইচআর সিস্টেমের সাথে মেলান',
  'org.syncSub': 'কোনো কানেক্টর বসাতে হবে না। একটি কী তৈরি করুন, আর Workday, SuccessFactors বা যেকোনো শিডিউলার দিয়ে রোস্টার পাঠান — এটি নতুন কার্ড না বানিয়ে বিদ্যমান তথ্য হালনাগাদ করে, তাই প্রতিবার পুরো তালিকাই পাঠাতে পারেন।',
  'org.brandTitle': 'নাম ও লোগো',
  'org.brandBody': 'প্রতিষ্ঠানের নাম ও চিহ্ন, যেভাবে এর ইস্যু করা প্রতিটি কার্ডে দেখা যায়।',
  'org.logoAdd': 'লোগো যোগ করুন',
  'org.logoChange': 'লোগো বদলান',
  'org.logoRemove': 'সরান',
  'org.logoAlt': 'প্রতিষ্ঠানের লোগো',
  'org.logoHint': 'PNG, JPEG বা WebP। এটি কখনো ছাঁটা হয় না — পুরোটাই বসে — এবং ছোট আকারে দেখা যায়, তাই সরল চিহ্নই সবচেয়ে ভালো।',
  'org.brandSaved': 'সংরক্ষিত হয়েছে।',
  'org.sharedTitle': 'প্রতিটি কার্ডে থাকা যোগাযোগ',
  'org.sharedBody': 'সর্বোচ্চ তিনটি প্রতিষ্ঠান-ব্যাপী যোগাযোগ — একটি সুইচবোর্ড, একটি সহায়তা ঠিকানা — প্রতিষ্ঠানের ইস্যু করা প্রতিটি কার্ডে থাকে। কর্মীরা এগুলো সরাতে পারেন না।',
  'org.sharedLabel': 'লেবেল',
  'org.sharedLabelPh': 'সুইচবোর্ড',
  'org.sharedValue': 'ফোন, ইমেইল বা ঠিকানা',
  'org.sharedValuePh': '+৮৮০ ৯৬০০ ০০০০০০',
  'org.sharedAdd': 'যোগাযোগ যোগ করুন',
  'org.sharedRemove': 'এই যোগাযোগটি সরান',
  'org.syncMode': 'এই তালিকাই পুরো দল — তালিকায় নেই এমন সবার কার্ড বাতিল করুন',
  'org.syncModeWarn': 'প্রতিষ্ঠানের ইস্যু করা কার্ডধারীদের মধ্যে যাঁরা এই তালিকায় নেই, তাঁদের কার্ড বাতিল হবে — ট্যাগ সঙ্গে সঙ্গে বন্ধ হয়ে যাবে। শুধু সম্পূর্ণ তালিকার ক্ষেত্রেই ব্যবহার করুন।',
  'org.syncRevoked': 'তালিকায় আর নেই এমন {n} জনের কার্ড বাতিল হয়েছে:',
  'org.syncRevokedOne': 'তালিকায় আর নেই এমন ১ জনের কার্ড বাতিল হয়েছে:',
  'org.department': 'বিভাগ',
  'org.personDeptPh': 'কার্ডিওলজি',
  'org.keyOnce': 'এখনই কপি করুন — এটি হ্যাশ করে রাখা হয়, আর দেখানো যাবে না। নতুন কী বানালে এটি সঙ্গে সঙ্গে বাতিল হয়ে যাবে।',
  'org.newKey': 'নতুন কী তৈরি করুন',
  'org.genKey': 'কী তৈরি করুন',
  'org.badPaste': 'যোগ করার মতো কিছু নেই — ফরম্যাটটি দেখুন।',
  'org.usage': 'কার্ডগুলো কীভাবে ব্যবহার হচ্ছে',
  'org.usageNone': 'এখনও কোনো ট্যাপ হয়নি। কেউ ট্যাগে ট্যাপ করলে বা কোড স্ক্যান করলে গোনা শুরু হবে।',
  'org.statTaps': '{days} দিনে ট্যাপ',
  'org.statUsed': '{total}টির মধ্যে {n}টি কার্ডে অন্তত একবার ট্যাপ হয়েছে',
  'org.statUnclaimed': '{n}টি এখনও নিশ্চিত করা হয়নি',
  'org.remind': 'লিংকটি আবার পাঠান',
  'org.reminded': 'পাঠানো হয়েছে। তাঁদের অফিসের ঠিকানায় কোড যাবে।',
  'org.owns': 'প্রতিটি কার্ডে প্রতিষ্ঠান যা নিয়ন্ত্রণ করে',
  'org.ownsBody': 'তালাবদ্ধ ঘরগুলো সার্ভারেই আটকানো হয় — কর্মী বদলাতে চাইলে সরাসরি প্রত্যাখ্যাত হয়, শুধু ধূসর দেখায় না। বাকি সবকিছু তাঁদেরই থাকে।',
  'org.fld.name': 'নাম',
  'org.fld.title': 'পদবি',
  'org.fld.company': 'প্রতিষ্ঠান',
  'org.fld.phone': 'ফোন',
  'org.fld.email': 'ইমেইল',
  'org.fld.photo': 'ছবি',
  'org.ownsSaved': 'সংরক্ষিত। এখন থেকে তৈরি হওয়া কার্ডে প্রযোজ্য।',
  'org.searchTeam': '{n} জনের মধ্যে খুঁজুন',
  'org.fAll': 'সবাই',
  'org.fActive': 'সচল',
  'org.fUnclaimed': 'অদাবিকৃত',
  'org.fRevoked': 'বাতিল',
  'org.showMore': 'আরও {n} জন দেখুন',
  'org.showingOf': '{total} জনের মধ্যে {n} জন দেখানো হচ্ছে',
  'org.noMatch': 'এর সাথে কেউ মিলল না।',
  'org.admins': 'এই কার্ডগুলো কারা পরিচালনা করতে পারবেন',
  'org.adminsBody': 'যিনি প্রতিষ্ঠানটি চালু করেছেন, প্রতিষ্ঠান তাঁর চেয়ে বেশি দিন থাকে। দ্বিতীয় একজন ব্যবস্থাপক যোগ করুন, যাতে কেউ চলে গেলে কার্ডগুলো আটকে না যায়।',
  'org.aloneWarn': 'আপনিই একমাত্র ব্যবস্থাপক। আপনি চলে গেলে কেউ কার্ড বাতিল করতে, অন্যকে দিতে বা এই সেটিংস বদলাতে পারবেন না।',
  'org.addAdmin': 'ব্যবস্থাপক যোগ করুন',
  'org.adminEmail': 'তাঁর অফিসের ইমেইল',
  'org.you': 'আপনি',
  'org.removeAdmin': '{name}-কে সরান',
  'org.stepDown': 'দায়িত্ব ছাড়ুন',
  'org.link': 'কার্ডের লিংক',
  'org.copyLink': 'লিংক কপি করুন',
  'org.copied2': 'কপি হয়েছে',
  'org.download': 'লিংকগুলো নামান (সিএসভি)',
  'org.downloadHint': 'প্রতিটি কার্ডের লিংক — ট্যাগে লেখা বা বিলি করার জন্য।',
  'org.printAll': 'সব কার্ড প্রিন্ট করুন',
  'org.printAllHint': 'প্রতিটি সচল কার্ডের চেহারা, প্রতি পৃষ্ঠায় একটি, আসল মাপে (৮৫×৫৫ মিমি)।',
  'org.transferTitle': '{name}-এর কার্ডটি অন্য কাউকে দিন',
  'org.transferBody': 'ট্যাগটি কাজ করতেই থাকবে — শুধু এর পেছনের মানুষটি বদলাবে।',
  'org.transferTo': 'তাঁর অফিসের ইমেইল',
  'org.transferGo': 'হস্তান্তর করুন',
  'org.revokeTitle': '{name}-এর কার্ডটি বাতিল করবেন?',
  'org.revokeBody': 'তাঁর ট্যাগ আর কাজ করবে না। ট্যাপ করলে মনে হবে কার্ডটি কখনও ছিলই না।',
  'org.revokeGo': 'বাতিল করুন',
  'org.cancel': 'বাতিল',
  'org.setUp': 'প্রাতিষ্ঠানিক কার্ড চালু করুন',
  'org.details': 'প্রতিষ্ঠানের বিবরণ ও সংযোগ',
  'org.step1': 'প্রতিষ্ঠান',
  'org.step2': 'যাচাই',
  'org.step3': 'আপনার দল',
  'org.stepOf': '৩টির মধ্যে {n} নম্বর ধাপ',
  'org.copy': 'কপি',
  'org.copied': 'কপি হয়েছে',
  'org.dnsHost': 'নাম / হোস্ট',
  'org.dnsValue': 'মান',
  'org.dnsWhere': '{domain}-এর ডিএনএস যেখানে পরিচালিত হয় সেখানে। পরিবর্তন দেখাতে সাধারণত কয়েক মিনিট লাগে।',
  'org.willSkip': '{n}টি বাদ যাবে — সার্ভার কেবল {domain}-এ কার্ড দেয়।',
  'org.willSkipOne': '১টি বাদ যাবে — সার্ভার কেবল {domain}-এ কার্ড দেয়।',
  'org.laterNote': 'কার্ড দুভাবেই কাজ করে — ডোমেইন যাচাই না হওয়া পর্যন্ত শুধু যাচাইয়ের চিহ্নটি থাকবে না।',
  'org.willCreate': '{n}টি কার্ড তৈরি হবে',
  'org.willCreateOne': '১টি কার্ড তৈরি হবে',
  'org.nothingYet': 'উপরে আপনার দলের তথ্য দিন, এখানে আগে দেখা যাবে।',
  'org.previewTitle': 'যা তৈরি হতে যাচ্ছে',
  'org.offDomain': '{domain}-এ নয়',
  'org.teamCount': '{n} জন',
  'org.teamCountOne': '১ জন',
  'org.domainHint': 'কর্মীরা এখানে পাঠানো কোড দিয়ে তাঁদের কার্ড নিশ্চিত করবেন।',
  'org.auditTitle': 'কার্যক্রমের লগ',
  'org.auditSub': 'ব্যবস্থাপকেরা যা করেছেন, নতুনটি আগে। স্বয়ংক্রিয়ভাবে রাখা হয় এবং বদলানো যায় না।',
  'org.auditEmpty': 'এখনও কিছু নেই।',
  'org.auditByKey': 'এপিআই কী দিয়ে',
  'org.auditBySystem': 'সিস্টেম',
  'org.auditUnknown': 'কাজ',
  'org.audit.org.created': 'প্রতিষ্ঠান তৈরি হয়েছে',
  'org.audit.org.updated': 'প্রতিষ্ঠানের তথ্য বদলেছে',
  'org.audit.org.domain_verified': 'ডোমেইন যাচাই হয়েছে',
  'org.audit.org.key_issued': 'এপিআই কী দেওয়া হয়েছে',
  'org.audit.roster.imported': 'কর্মী যোগ করা হয়েছে',
  'org.audit.card.revoked': 'কার্ড বাতিল হয়েছে',
  'org.audit.card.transferred': 'কার্ড হস্তান্তর হয়েছে',
  'org.audit.card.reminded': 'মনে করিয়ে দেওয়া হয়েছে',
  'org.audit.admin.added': 'ব্যবস্থাপক যোগ হয়েছে',
  'org.audit.admin.removed': 'ব্যবস্থাপক সরানো হয়েছে',
  'org.audit.event.created': 'ইভেন্ট তৈরি হয়েছে',
  'org.audit.event.updated': 'ইভেন্ট পরিবর্তিত হয়েছে',
  'org.audit.event.deleted': 'ইভেন্ট মুছে ফেলা হয়েছে',
  'org.audit.event.ended': 'ইভেন্ট শেষ হয়েছে',
  'org.audit.event.attendance_read': 'উপস্থিতি ডাউনলোড হয়েছে',
  'org.switchLabel': 'কোন প্রতিষ্ঠান',
  'org.employees': 'কর্মী',
  'org.addPeople': 'লোক যোগ করুন',
  'org.uploadCsv': 'সিএসভি আপলোড করুন',
  'org.orPaste': 'অথবা নিচে পেস্ট করুন',
  'org.dropHint': 'সিএসভি বা টিএসভি — সরাসরি আপনার এইচআর সিস্টেম, এক্সেল বা গুগল শিট থেকে। ফাইলে হেডার সারি থাকলে কলামের ক্রম যাই হোক, সমস্যা নেই।',
  'org.addOne': 'বদলে একজনের তথ্য টাইপ করুন',
  'org.backToBulk': 'বদলে একটি তালিকা আনুন',
  'org.headerFound': 'প্রথম সারিটি কলামের নাম হিসেবে ধরা হয়েছে।',
  'org.badFile': 'ফাইলটি পড়া গেল না।',
  'org.skippedLines': '{n}টি লাইন ব্যবহার করা যায়নি',
  'org.skippedLinesOne': '১টি লাইন ব্যবহার করা যায়নি',
  'org.skipNoEmail': 'ইমেইল নেই',
  'org.skipNoName': 'নাম নেই',
  'org.line': 'লাইন {n}',
  'org.addPerson': 'আরও একজনকে যোগ করুন',
  'org.removePerson': 'এই ব্যক্তিকে সরান',
  'org.workEmail': 'অফিসের ইমেইল',
  'org.jobTitle': 'পদবি',
  'org.optional': 'ঐচ্ছিক',
  'org.personNamePh': 'নুসরাত রহমান',
  'org.personEmailPh': 'nusrat@pharmavita.com',
  'org.personTitlePh': 'মেডিকেল সায়েন্স লিয়াজোঁ',
  'org.personPhonePh': '+880 1712 345678',
  'org.pasteSub': 'প্রত্যেকে একটি অদাবিকৃত কার্ড পাবেন। তাঁদের অফিসের ঠিকানায়{on} পাঠানো কোড দিয়ে তাঁরা কার্ডটি নিজের করে নেবেন — মনে রাখার কিছু নেই, আপনাকে কিছু বিলি করতেও হবে না।',
  'org.onDomain': ' ({domain}-এ)',
  'org.creating': 'তৈরি হচ্ছে…',
  'org.createCards': 'তাঁদের কার্ড তৈরি করুন',
  'org.cardsCreated': 'কার্ড তৈরি হয়েছে।',
  'org.cardRevoked': 'কার্ড বাতিল হয়েছে।',
  'org.cardTransferred': 'কার্ড হস্তান্তর হয়েছে।',
  'org.transferAria': '{name}-এর কার্ড হস্তান্তর করুন',
  'org.revokeAria': '{name}-এর কার্ড বাতিল করুন',
  'org.confirmRevoke': '{name}-এর কার্ডটি বাতিল করবেন? তাঁর ট্যাগটি আর কাজ করবে না।',
  'org.promptTransfer': '{name}-এর কার্ডটি কোন অফিস-ঠিকানায় দেবেন?\n\nট্যাগটি কাজ করতেই থাকবে — শুধু এর পেছনের মানুষটি বদলাবে।',
  'org.skippedLine': 'লাইন {row} ({email}): {reason}',

  'lang.title': 'ভাষা',
};

const CATALOGS: Record<Locale, Record<MessageKey, string>> = { en, bn };

/** Locale→BCP-47 tag, for Intl. bn-BD rather than bare bn: it picks Bengali
 *  digits and the date order people in Bangladesh actually read. */
const INTL_TAG: Record<Locale, string> = { en: 'en', bn: 'bn-BD' };

function detect(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as Locale | null;
    if (saved && saved in CATALOGS) return saved;
  } catch { /* private mode — fall through to the browser's preference */ }
  const nav = typeof navigator !== 'undefined' ? navigator.languages ?? [navigator.language] : [];
  for (const tag of nav) {
    const base = (tag || '').toLowerCase().split('-')[0];
    if (base in CATALOGS) return base as Locale;
  }
  return DEFAULT;
}

let current: Locale = typeof window === 'undefined' ? DEFAULT : detect();
const listeners = new Set<() => void>();

/** The active interface language. */
export function locale(): Locale { return current; }

/** The BCP-47 tag for Intl formatting in the active language. */
export function intlTag(): string { return INTL_TAG[current]; }

/** Switch language. Persists, updates <html lang>, and re-renders every
 *  component that uses the controller — no reload. */
export function setLocale(next: Locale) {
  if (!(next in CATALOGS) || next === current) return;
  current = next;
  try { localStorage.setItem(STORAGE_KEY, next); } catch { /* private mode */ }
  if (typeof document !== 'undefined') document.documentElement.lang = next;
  listeners.forEach((fn) => fn());
}

/**
 * Translate a key, filling {placeholders}.
 *
 * A missing translation falls back to English rather than showing the key: a
 * half-translated screen is usable, and `dir.empty` on a button is not.
 */
export function t(key: MessageKey, vars?: Record<string, string | number>): string {
  const s = CATALOGS[current][key] ?? en[key] ?? key;
  if (!vars) return s;
  return s.replace(/\{(\w+)\}/g, (m, name) =>
    name in vars ? String(vars[name]) : m);
}

/**
 * Like `t`, but placeholders may be markup — `tx('claim.sentTo', { where:
 * html`<span class="addr">…</span>` })`.
 *
 * The alternative, splitting a sentence into "We sent a code to" + address +
 * "— from this card", only works while every language puts the pieces in that
 * order. Bangla does not: the address lands mid-sentence with the verb at the
 * end. Substituting into the whole translated sentence lets each language put
 * the slot where it belongs.
 */
export function tx(key: MessageKey, vars: Record<string, unknown>): unknown[] {
  const s = CATALOGS[current][key] ?? en[key] ?? key;
  // Split on the placeholders themselves, so what is left is literal text and
  // what was matched is a value — in whatever order the translation used.
  return s.split(/(\{\w+\})/g).map((part) => {
    const m = /^\{(\w+)\}$/.exec(part);
    return m && m[1] in vars ? vars[m[1]] : part;
  });
}

/** Format a number in the active language — Bengali digits under bn.
 *  NEVER use this for a phone number: those are dialled, not read. */
export function n(value: number): string {
  try { return value.toLocaleString(intlTag()); } catch { return String(value); }
}

/** Every message key the app can ask for. Exists so a test can enumerate the
 *  catalog: a message added without a translation then fails the build rather
 *  than shipping in English. */
export function messageKeys(): MessageKey[] {
  return Object.keys(en) as MessageKey[];
}

/** The raw string for a key in a given language, bypassing the active one.
 *  For checking catalogs, not for rendering. */
export function messageIn(loc: Locale, key: MessageKey): string {
  return CATALOGS[loc][key];
}

/** A Lit controller that re-renders its host when the language changes. */
export class LocaleController implements ReactiveController {
  private onChange: () => void;
  constructor(private host: ReactiveControllerHost) {
    this.onChange = () => this.host.requestUpdate();
    host.addController(this);
  }
  hostConnected() { listeners.add(this.onChange); }
  hostDisconnected() { listeners.delete(this.onChange); }
}

// Set <html lang> at load so screen readers and hyphenation are right from the
// first paint, not only after a switch.
if (typeof document !== 'undefined') document.documentElement.lang = current;
